import { useState, useMemo, useEffect, useCallback } from 'react';
import feedbackService from '../../../../services/feedback.service';
import { debugLog, debugWarn, debugError } from '../../../../utils/debugLog';

/**
 * useExecutionHistory Hook
 * Manages state and logic for execution history dialog
 */
export const useExecutionHistory = (open, templateId, executions) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [feedbackMap, setFeedbackMap] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Load feedback
  const loadFeedback = useCallback(async () => {
    if (!templateId) {
      debugLog('🔍 ExecutionHistoryDialog: No templateId provided');
      return;
    }

    try {
      debugLog(
        '🔍 ExecutionHistoryDialog: Loading feedback for templateId:',
        templateId
      );
      const feedbackData =
        await feedbackService.getTemplateFeedback(templateId);
      debugLog(
        '🔍 ExecutionHistoryDialog: Feedback data received:',
        feedbackData
      );

      const feedbackByExecution = {};
      if (Array.isArray(feedbackData)) {
        debugLog(
          '🔍 ExecutionHistoryDialog: Processing array of',
          feedbackData.length,
          'feedback items'
        );
        feedbackData.forEach(feedback => {
          if (feedback.execution_id) {
            feedbackByExecution[feedback.execution_id] = feedback;
            debugLog(
              '🔍 ExecutionHistoryDialog: Mapped feedback for execution_id:',
              feedback.execution_id
            );
          }
        });
      } else if (
        feedbackData.feedback &&
        Array.isArray(feedbackData.feedback)
      ) {
        debugLog(
          '🔍 ExecutionHistoryDialog: Processing feedback array from object'
        );
        feedbackData.feedback.forEach(feedback => {
          if (feedback.execution_id) {
            feedbackByExecution[feedback.execution_id] = feedback;
            debugLog(
              '🔍 ExecutionHistoryDialog: Mapped feedback for execution_id:',
              feedback.execution_id
            );
          }
        });
      } else {
        debugWarn(
          '🔍 ExecutionHistoryDialog: Unexpected feedback data format:',
          feedbackData
        );
      }

      debugLog(
        '🔍 ExecutionHistoryDialog: Feedback map created:',
        feedbackByExecution
      );
      debugLog(
        '🔍 ExecutionHistoryDialog: Execution IDs in dialog:',
        executions?.map(e => e.id)
      );
      setFeedbackMap(feedbackByExecution);
    } catch (error) {
      debugError('❌ ExecutionHistoryDialog: Failed to load feedback:', error);
    }
  }, [templateId, executions]);

  // Fetch feedback when dialog opens
  useEffect(() => {
    if (open && templateId) {
      loadFeedback();
    }
  }, [open, templateId, loadFeedback]);

  // Sort and paginate executions
  const sortedAndPaginatedExecutions = useMemo(() => {
    if (!executions || executions.length === 0) return [];

    const sorted = [...executions].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortBy === 'execution_time' || sortBy === 'cost') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sorted.slice(startIndex, endIndex);
  }, [executions, page, rowsPerPage, sortBy, sortOrder]);

  const toggleRowExpansion = executionId => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(executionId)) {
        newSet.delete(executionId);
      } else {
        newSet.add(executionId);
      }
      return newSet;
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedRows(new Set());
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRows(new Set());
  };

  const handleSortChange = event => {
    const newSortBy = event.target.value;
    if (newSortBy === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setPage(0);
    setExpandedRows(new Set());
  };

  return {
    expandedRows,
    feedbackMap,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    sortedAndPaginatedExecutions,
    toggleRowExpansion,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSortChange,
  };
};
